import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import TemplateSelector from "./TemplateSelector";
import { Template } from "@/types";
import { useTranslation } from "react-i18next";

interface TemplateSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: Template) => void;
  selectedTemplateId?: string;
}

const TemplateSelectorModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedTemplateId
}: TemplateSelectorModalProps) => {
  const { t } = useTranslation();
  const [localSelectedTemplate, setLocalSelectedTemplate] = useState<Template | null>(null);

  const handleTemplateSelect = (template: Template) => {
    setLocalSelectedTemplate(template);
  };

  const handleConfirm = () => {
    if (localSelectedTemplate) {
      onSelect(localSelectedTemplate);
      onClose();
      setLocalSelectedTemplate(null);
    }
  };

  const handleCancel = () => {
    onClose();
    setLocalSelectedTemplate(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-full max-h-full w-full h-full m-0 rounded-none sm:max-w-4xl sm:max-h-[80vh] sm:w-auto sm:h-auto sm:m-6 sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-4 py-3 sm:px-6 sm:py-4">
          <DialogTitle className="text-lg font-semibold">{t('templates.selector.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6">
          <TemplateSelector
            onSelect={handleTemplateSelect}
            initialTemplateId={selectedTemplateId}
          />
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-card sm:px-6">
          <Button variant="outline" onClick={handleCancel} className="flex-1 sm:flex-none">
            {t('templates.selector.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!localSelectedTemplate}
            className="flex-1 sm:flex-none"
          >
            {t('templates.selector.select')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectorModal;